import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Form, Modal, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import apiService from '../services/api';

const OrganizationAccessRights = ({ organizationId, organizationName, onClose }) => {
  const [accessRights, setAccessRights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRight, setEditingRight] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [saving, setSaving] = useState(false);
  const [sortField, setSortField] = useState('code');
  const [sortDirection, setSortDirection] = useState('asc');

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    isModuleLevel: false
  });

  useEffect(() => {
    if (organizationId) {
      loadAccessRights();
    }
  }, [organizationId, currentPage, search, itemsPerPage]);

  const loadAccessRights = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiService.getOrganizationAccessRights(organizationId, currentPage, itemsPerPage, search);
      if (response.success) {
        setAccessRights(response.data || []);
        setTotalPages(Math.ceil((response.pagination?.total || 0) / itemsPerPage));
      } else {
        setError(response.error || 'Failed to load access rights');
      }
    } catch (error) {
      console.error('Error loading access rights:', error);
      setError('Failed to load access rights');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      setCurrentPage(1);
      setSearch(e.target.value);
    }
  };

  const handleShowModal = (accessRight = null) => {
    if (accessRight) {
      setEditingRight(accessRight);
      setFormData({
        code: accessRight.code || '',
        description: accessRight.description || '',
        isModuleLevel: accessRight.is_module_level || false
      });
    } else {
      setEditingRight(null);
      setFormData({
        code: '',
        description: '',
        isModuleLevel: false
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRight(null);
    setFormData({
      code: '',
      description: '',
      isModuleLevel: false
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.code.trim() || !formData.description.trim()) {
      setError('Code and description are required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const accessRightData = {
        organization_id: organizationId,
        code: formData.code.trim(),
        description: formData.description.trim(),
        is_module_level: formData.isModuleLevel
      };

      let response;
      if (editingRight) {
        response = await apiService.updateOrganizationAccessRight(editingRight.id, accessRightData);
      } else {
        response = await apiService.createOrganizationAccessRight(accessRightData);
      }

      if (response.success) {
        handleCloseModal();
        await loadAccessRights();
      } else {
        setError(response.error || 'Failed to save access right');
      }
    } catch (error) {
      console.error('Error saving access right:', error);
      setError('Failed to save access right');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Are you sure you want to delete the access right "${code}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiService.deleteOrganizationAccessRight(id);
      if (response.success) {
        await loadAccessRights();
      } else {
        setError(response.error || 'Failed to delete access right');
      }
    } catch (error) {
      console.error('Error deleting access right:', error);
      setError('Failed to delete access right');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted ms-1"></i>;
    }
    return sortDirection === 'asc' 
      ? <i className="bi bi-arrow-up text-primary ms-1"></i>
      : <i className="bi bi-arrow-down text-primary ms-1"></i>;
  };

  const sortedAccessRights = React.useMemo(() => {
    const filtered = accessRights.filter(right =>
      right.code.toLowerCase().includes(search.toLowerCase()) ||
      right.description.toLowerCase().includes(search.toLowerCase())
    );

    return [...filtered].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'code':
          aValue = a.code.toLowerCase();
          bValue = b.code.toLowerCase();
          break;
        case 'description':
          aValue = a.description.toLowerCase();
          bValue = b.description.toLowerCase();
          break;
        case 'levelType':
          aValue = a.is_module_level ? 'module level' : 'feature level';
          bValue = b.is_module_level ? 'module level' : 'feature level';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [accessRights, search, sortField, sortDirection]);

  const filteredAccessRights = sortedAccessRights;

  return (
    <div>
      <style>
        {`
          .sortable-header:hover {
            background-color: rgba(0, 123, 255, 0.1) !important;
          }
          .sortable-header {
            transition: background-color 0.2s ease;
          }
        `}
      </style>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-shield-check me-2"></i>
              Access Rights for {organizationName}
            </h2>
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={() => handleShowModal()}>
                <i className="bi bi-plus-circle me-2"></i>
                Add Access Right
              </Button>
              {onClose && (
                <Button variant="outline-secondary" onClick={onClose}>
                  <i className="bi bi-arrow-left me-2"></i>
                  Back to Organizations
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Search and Filter */}
      <Row className="mb-4">
        <Col md={6}>
          <Form.Control
            type="text"
            placeholder="Search access rights..."
            onKeyDown={handleSearch}
            onBlur={handleSearch}
          />
        </Col>
        <Col md={6} className="text-end">
          <Form.Select 
            style={{ width: 'auto', display: 'inline-block' }}
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
          </Form.Select>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : filteredAccessRights.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-shield-check display-1"></i>
                  <div className="mt-2">No access rights found</div>
                  {search && (
                    <div className="mt-1 small">Try adjusting your search criteria</div>
                  )}
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th 
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('code')}
                        className="sortable-header"
                      >
                        Code
                        {getSortIcon('code')}
                      </th>
                      <th 
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('description')}
                        className="sortable-header"
                      >
                        Description
                        {getSortIcon('description')}
                      </th>
                      <th 
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('levelType')}
                        className="sortable-header"
                      >
                        Level Type
                        {getSortIcon('levelType')}
                      </th>
                      <th>Organization</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAccessRights.map((right) => (
                      <tr key={right.id}>
                        <td>
                          <code className="fw-medium">{right.code}</code>
                        </td>
                        <td>
                          <div className="text-wrap" style={{ maxWidth: '300px' }}>
                            {right.description}
                          </div>
                        </td>
                        <td>
                          <Badge bg={right.is_module_level ? 'primary' : 'secondary'}>
                            {right.is_module_level ? 'Module Level' : 'Feature Level'}
                          </Badge>
                        </td>
                        <td>
                          <span className="text-muted">{organizationName}</span>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleShowModal(right)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(right.id, right.code)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <Card.Footer>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAccessRights.length)} of {filteredAccessRights.length} entries
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Prev 
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next 
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingRight ? 'Edit Access Right' : 'Add Access Right'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Code *</Form.Label>
                  <Form.Control
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="e.g., USER_MANAGEMENT, BILLING_VIEW"
                    required
                  />
                  <Form.Text className="text-muted">
                    Unique identifier for this access right (use uppercase with underscores)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Level Type</Form.Label>
                  <Form.Check
                    type="checkbox"
                    name="isModuleLevel"
                    checked={formData.isModuleLevel}
                    onChange={handleInputChange}
                    label="Module Level Access"
                  />
                  <Form.Text className="text-muted">
                    Check if this is a module-level access right (unchecked for feature-level)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe what this access right allows users to do..."
                required
              />
              <Form.Text className="text-muted">
                Provide a clear description of the permissions this access right grants
              </Form.Text>
            </Form.Group>

            <Alert variant="info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Organization:</strong> {organizationName}
              <div className="small mt-1">
                This access right will be created specifically for this organization.
              </div>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Access Right'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default OrganizationAccessRights;
